import { z } from 'zod';
import { API_SCOPES } from '../webhooks/events.js';
import { IsoDateTime, Uuid, patchOf } from './common.js';
/** Module `api-clients` (docs/modules/api-clients.md, ADR 0019). Owner: task S18. */
export const ApiScope = z.enum(API_SCOPES);
export const ApiClient = z.object({
    id: Uuid,
    name: z.string(),
    /** External system this client speaks for (e.g. 'omp'); used for matching and echo protection. */
    system: z.string().nullable(),
    scopes: z.array(ApiScope),
    /** null = every site */
    siteIds: z.array(Uuid).nullable(),
    /** Group used when a sign-in names no group. */
    defaultGroupId: Uuid.nullable(),
    /** Groups whose members this client may match and sign in. */
    groupIds: z.array(Uuid),
    status: z.enum(['active', 'revoked']),
    /** e.g. csk_ABCD…; the secret is never shown again. */
    keyId: z.string(),
    ownerModule: z.string().nullable(),
    lastUsedAt: IsoDateTime.nullable(),
    /** Phase 4 (ADR 0027): null or absent = never expires. After this moment the key is refused with 401. */
    expiresAt: IsoDateTime.nullable().optional(),
    /** Phase 4: null or absent = platform default; a value may only lower it. */
    ratePerMinute: z.number().int().nullable().optional(),
    createdAt: IsoDateTime,
});
export const ApiClientCreate = z.object({
    name: z.string().trim().min(1).max(100),
    system: z.string().trim().regex(/^[a-z][a-z0-9_-]{1,29}$/).nullable().default(null),
    scopes: z.array(ApiScope).min(1),
    siteIds: z.array(Uuid).max(500).nullable().default(null),
    defaultGroupId: Uuid.nullable().default(null),
    groupIds: z.array(Uuid).max(200).default([]),
    expiresAt: IsoDateTime.nullable().optional(),
    ratePerMinute: z.number().int().min(1).max(100000).nullable().optional(),
});
export const ApiClientUpdate = patchOf(ApiClientCreate.omit({ system: true }));
/** Returned once, on create and on "new secret". `apiKey` = `csk_<keyId>.<secret>`. */
export const ApiClientWithKey = z.object({ client: ApiClient, apiKey: z.string() });
export const ExternalLinkEntity = z.enum(['member', 'site', 'preregistration']);
export const ExternalLink = z.object({
    system: z.string(),
    entity: ExternalLinkEntity,
    entityId: Uuid,
    externalId: z.string(),
    /** Member or site name, for the list. */
    entityName: z.string(),
    createdAt: IsoDateTime,
});
export const ExternalLinkSet = z.object({
    system: z.string().max(30),
    entity: ExternalLinkEntity,
    entityId: Uuid,
    externalId: z.string().trim().min(1).max(100),
});
export const UnmatchedPerson = z.object({
    id: Uuid,
    system: z.string(),
    externalId: z.string(),
    name: z.string(),
    emailMasked: z.string().nullable(),
    firstSeenAt: IsoDateTime,
    lastSeenAt: IsoDateTime,
    attempts: z.number().int(),
});
/** Resolve an unmatched person: link to an existing member, or create one in a group. */
export const UnmatchedResolve = z.discriminatedUnion('action', [
    z.object({ action: z.literal('link'), memberId: Uuid }),
    z.object({ action: z.literal('create'), groupId: Uuid }),
    z.object({ action: z.literal('dismiss') }),
]);
//# sourceMappingURL=api-clients.js.map